import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { APP_VERSION } from "@/config/version";
import { confirmDeviceAuthorization, confirmRedirectWithWindowLocation } from "@/services";
import { getAuthBasePath } from "@/services/Auth/basepath-service";
import { getSessionId } from "@/services/Session/session-service";
import { Check, X } from "lucide-react";
import { useEffect, useState } from "react";

const SuccessScreen = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [confirmationStatus, setConfirmationStatus] = useState<
    "loading" | "success" | "error"
  >("loading");
  const [errorMessage, setErrorMessage] = useState("");
  const [countdown, setCountdown] = useState(2);
  const [sessionId, setSessionId] = useState<string>("");
  const [isDeviceAuthFlow, setIsDeviceAuthFlow] = useState(false);

  useEffect(() => {
    const handleConfirm = async () => {
      try {
        // ✅ Verificar se estamos no fluxo de device-auth
        const authBasePath = getAuthBasePath();
        const isDeviceAuth = authBasePath === '/device-auth';
        setIsDeviceAuthFlow(isDeviceAuth);

        // ✅ Obter sessionId do localStorage usando o serviço correto
        const currentSessionId = getSessionId();
        
        if (!currentSessionId) {
          throw new Error("SessionId não encontrado. Verifique se você acessou a aplicação corretamente.");
        }
        
        setSessionId(currentSessionId);
        console.log("SessionId obtido:", currentSessionId);
        console.log("Fluxo device-auth:", isDeviceAuth);

        // ✅ Processar confirmação baseado no tipo de fluxo
        if (isDeviceAuth) {
          // Fluxo de device-auth: chamar endpoint de confirmação
          await confirmDeviceAuthorization();
        } else {
          // Fluxo normal: simular loading
          await new Promise(resolve => setTimeout(resolve, 2000)); // 2 segundos de loading
        }

        // ✅ Se chegou até aqui, confirmação foi bem-sucedida
        setConfirmationStatus("success");
        setIsLoading(false);

      } catch (error: any) {
        console.error("Erro ao confirmar ativação:", error);

        // Extrair mensagem de erro da resposta
        const errorMsg =
          error.response?.data?.message ||
          error.message ||
          "Erro ao confirmar ativação.";
        setErrorMessage(errorMsg);
        setConfirmationStatus("error");
        setIsLoading(false);
      }
    };

    handleConfirm();
  }, []);

  // ✅ Contador de 3 segundos quando sucesso e redirecionamento automático
  useEffect(() => {
    if (confirmationStatus === "success" && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
    
    // ✅ Redirecionar quando o countdown chegar a 0 (apenas no fluxo normal)
    if (confirmationStatus === "success" && countdown === 0 && sessionId && !isDeviceAuthFlow) {
      try {
        confirmRedirectWithWindowLocation(sessionId);
      } catch (error) {
        console.error("Erro ao redirecionar:", error);
        setConfirmationStatus("error");
        setErrorMessage("Erro ao redirecionar. Tente novamente.");
      }
    }
    
    // ✅ No fluxo de device-auth, não redirecionar, apenas mostrar mensagem de sucesso
    if (confirmationStatus === "success" && isDeviceAuthFlow && countdown === 0) {
      console.log("Dispositivo autorizado com sucesso. Aguardando confirmação...");
    }
  }, [confirmationStatus, countdown, sessionId, isDeviceAuthFlow]);

  // ✅ Resto do componente permanece igual...
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background-gradient-primary">
        <div className="flex flex-col items-center justify-center min-h-screen px-5">
          <div className="w-full max-w-lg">
            <Card className="shadow-xl border-0 backdrop-blur-sm md:px-6 py-4 md:py-6 gap-0">
              <CardHeader className="pb-0">
                {/* Logo */}
                <div className="mb-6">
                  <img
                    src="/icon_oi_tv.svg"
                    alt="Oi TV"
                    className="h-12 w-auto object-contain mx-auto"
                  />
                </div>

                {/* Status Icon */}
                <div className="flex flex-col items-center justify-center py-8">
                  {confirmationStatus === "loading" && (
                    <>
                      <Spinner className="h-12 w-12 text-primary mb-4" />
                      <p className="text-lg font-medium text-gray-800">
                        Finalizando ativação
                      </p>
                    </>
                  )}

                  {confirmationStatus === "success" && (
                    <>
                      <div className="w-20 h-20 bg-primary rounded-full flex items-center justify-center mb-6">
                        <Check className="h-10 w-10 text-white" />
                      </div>
                      <div className="text-left space-y-2">
                        <h1 className="text-xl font-bold text-gray-800">
                          Sua conta de 2º tela foi criada com sucesso!
                        </h1>
                        <p className="text-sm text-gray-600">
                          Agora você já pode aproveitar seus canais preferidos
                          de onde quiser!
                        </p>
                      </div>
                    </>
                  )}

                  {confirmationStatus === "error" && (
                    <>
                      <div className="w-20 h-20 bg-red-500 rounded-full flex items-center justify-center mb-6">
                        <X className="h-10 w-10 text-white" />
                      </div>
                      <div className="text-center space-y-2">
                        <h1 className="text-xl font-bold text-gray-800">
                          Sua conta de 2º tela não foi criada
                        </h1>
                        <p className="text-sm text-gray-600">{errorMessage}</p>
                      </div>
                    </>
                  )}
                </div>
              </CardHeader>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-gradient-primary">
      <div className="flex flex-col items-center justify-center min-h-screen px-5">
        <div className="w-full max-w-lg">
          <Card className="shadow-xl border-0 backdrop-blur-sm md:px-6 py-4 md:py-6 gap-0">
            <CardHeader className="pb-0">
              {/* Logo */}
              <div className="mb-6">
                <img
                  src="/icon_oi_tv.svg"
                  alt="Oi TV"
                  className="h-12 w-auto object-contain mx-auto"
                />
              </div>

              {/* Status Icon */}
              <div className="flex flex-col items-center justify-center py-8">
                {confirmationStatus === "loading" && (
                  <>
                    <Spinner className="h-12 w-12 text-primary mb-4" />
                    <p className="text-lg font-medium text-gray-800">
                      Finalizando ativação
                    </p>
                  </>
                )}

                {confirmationStatus === "success" && (
                  <>
                    <div className="w-20 h-20 bg-primary rounded-full flex items-center justify-center mb-6">
                      <Check className="h-10 w-10 text-white" />
                    </div>
                    <div className="text-left space-y-2">
                      <h1 className="text-xl font-bold text-gray-800">
                        Sua conta de 2º tela foi criada com sucesso!
                      </h1>
                      <p className="text-sm text-gray-600">
                        Agora você já pode aproveitar seus canais preferidos de
                        onde quiser!
                      </p>
                    </div>
                  </>
                )}

                {confirmationStatus === "error" && (
                  <>
                    <div className="w-20 h-20 bg-red-500 rounded-full flex items-center justify-center mb-6">
                      <X className="h-10 w-10 text-white" />
                    </div>
                    <div className="text-left space-y-2">
                      <h1 className="text-xl font-bold text-gray-800">
                        Erro ao criar sua segunda tela
                      </h1>
                      <p className="text-sm text-gray-600">{errorMessage}</p>
                    </div>
                  </>
                )}
              </div>
            </CardHeader>

            <CardContent className="pt-6">
              {/* ✅ Contador com countdown - apenas no fluxo normal */}
              {confirmationStatus === "success" && !isDeviceAuthFlow && (
                <div className="text-center">
                  <p className="text-sm text-gray-500">
                    Redirecionando automaticamente em {countdown} segundos...
                  </p>
                </div>
              )}
              {/* ✅ Mensagem para fluxo de device-auth */}
              {confirmationStatus === "success" && isDeviceAuthFlow && (
                <div className="text-center">
                  <p className="text-sm text-gray-500">
                    Dispositivo autorizado! Você pode fechar esta janela.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="flex justify-center items-center mt-4">
            <span className="text-xs text-white">{APP_VERSION}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuccessScreen;
